import 'firebase/auth';
import { User as FirebaseAuthUser, getAuth } from 'firebase/auth';
import { EmailUser } from 'hydra-data-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { ApiClient } from '../api/ApiClient';
import { Loader } from '../components/LoaderComponent';
import { USER_TYPE } from '../constants';
import { Errors } from '../helpers/Errors';
import { isNonEmptyString } from '../helpers/StringUtils';
import { Analytics } from '../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../helpers/analytics/AnalyticsEvent';
import { AuthStoreAction, AuthStoreState } from '../store/AuthStore';
import { ActionType, ConnectInitializeUser } from '../store/StoreTypes';
import { AppStack } from './App/AppStack';
import { AuthStack } from './Auth/AuthStack';

type State = {
  loading: boolean;
};

type Props = {
  firebaseUser: FirebaseAuthUser | undefined;
  currentLoggedInUser: ConnectInitializeUser | EmailUser | undefined;
  hcpReferralHash: string | undefined;
  setFirebaseUser: (firebaseUser: FirebaseAuthUser | undefined) => {
    type: ActionType.SET_FIREBASE_USER;
    firebaseUser: FirebaseAuthUser | undefined;
  };
  setUserFirstTimeVisitStatus: (firstTimeVisit: boolean) => {
    type: ActionType.SET_USER_STATUS;
    userStatus: { firstTimeVisit: boolean };
  };
  authenticatingExistingMindsetId?: boolean;
  setHcpReferralHash: (hcpReferralHash: string | undefined) => {
    type: ActionType.SET_HCP_PATIENT_LINK_ID;
    hcpReferralHash: string | undefined;
  };
};

class StackSwitcherImp extends Component<Props, State> {
  private authListener?: () => any;

  constructor(props: Props) {
    super(props);
    this.onAuthStateChanged = this.onAuthStateChanged.bind(this);
  }

  state: State = {
    loading: true,
  };

  componentDidMount() {
    getAuth().onAuthStateChanged(this.onAuthStateChanged);
    const { setHcpReferralHash } = this.props;
    const referralHash = sessionStorage.getItem('hcpReferralHash') ?? undefined;
    if (isNonEmptyString(referralHash)) {
      setHcpReferralHash(referralHash);
    }
  }

  async componentDidUpdate() {
    const { currentLoggedInUser, firebaseUser, setUserFirstTimeVisitStatus } = this.props;

    // If user is signing in for the first time, show email verified modal and set newUser status to false
    if (firebaseUser?.emailVerified && currentLoggedInUser?.newUser) {
      setUserFirstTimeVisitStatus(true);

      await new ApiClient().updateHcpNewUserStatus();

      Analytics.track({
        event: AnalyticsEventName.a_email_verified,
        userType: USER_TYPE.HCP,
      });
    }
  }

  componentWillUnmount() {
    this.authListener = this.authListener?.();
  }

  private async onAuthStateChanged(user: FirebaseAuthUser | null) {
    const { setFirebaseUser } = this.props;
    setFirebaseUser(user ?? undefined);
    this.setState({ loading: false });
    Errors.setUser(user?.uid);
  }

  render() {
    const { loading } = this.state;
    const { authenticatingExistingMindsetId, firebaseUser, hcpReferralHash } = this.props;
    if (loading) {
      return <Loader />;
    }

    if (
      firebaseUser &&
      (firebaseUser.emailVerified || process.env.REACT_APP_IGNORE_EMAIL_VERIFICATION === 'true') &&
      !authenticatingExistingMindsetId &&
      !isNonEmptyString(hcpReferralHash)
    ) {
      return (
        <BrowserRouter>
          <AppStack />
        </BrowserRouter>
      ); //Show Application Stack
    }
    return (
      <BrowserRouter>
        <AuthStack />
      </BrowserRouter>
    ); // Show Auth Stack
  }
}

function mapStateToProps(state: AuthStoreState) {
  let linkedPatientsCount = 0;
  const hcpUser = state.user as ConnectInitializeUser;
  if (hcpUser !== undefined && Array.isArray(hcpUser.linkedPatients)) {
    linkedPatientsCount = hcpUser.linkedPatients.length;
  }
  return {
    linkedPatientsCount,
    authenticatingExistingMindsetId: state.authenticatingExistingMindsetId,
    firebaseUser: state.firebaseUser,
    currentLoggedInUser: state.user,
    hcpReferralHash: state.hcpReferralHash,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AuthStoreAction>) {
  return {
    setFirebaseUser: (firebaseUser: FirebaseAuthUser | undefined) =>
      dispatch({ type: ActionType.SET_FIREBASE_USER, firebaseUser }),
    setUserFirstTimeVisitStatus: (firstTimeVisit: boolean) =>
      dispatch({
        type: ActionType.SET_USER_STATUS,
        userStatus: { firstTimeVisit },
      }),
    setHcpReferralHash: (hcpReferralHash: string | undefined) =>
      dispatch({ type: ActionType.SET_HCP_PATIENT_LINK_ID, hcpReferralHash }),
  };
}

export const StackSwitcher = connect(mapStateToProps, mapDispatchToProps)(StackSwitcherImp);
